import { useCallback, useEffect, useRef, useState } from "react";
import { getShippingEstimate, getSaleSimulation } from "../../auth/shipping";

// Automatic shipping estimate from the backend (server_elakay
// elakay_services/shipping/estimator is the source of truth; this hook only
// fetches and caches its normalized result).
//
// - debounced (typing an address or clicking +/- on the quantity does not
//   fire one request per keystroke/click);
// - cached per (items, destination, signed-in) for a short time, so a
//   re-render or re-opening the checkout does not refetch;
// - while a new estimate is loading, `status` is "loading" and callers must
//   not show the previous amount (never a temporary $0 / Free Shipping);
//   `updating` tells a RE-calculation (address/variant/quantity changed after
//   a first result) from the first one, for an "Updating shipping…" label.
//
// The server applies the destination Sale Simulation inside the estimate:
// an amount it returns already includes any destination shipping adjustment.
const CACHE_TTL_MS = 2 * 60 * 1000;
const DEBOUNCE_MS = 350;
const cache = new Map();

export const buildEstimateItems = (products = []) => (Array.isArray(products) ? products : [])
  .filter((p) => p && (p.productID || p.productId))
  .map((p) => ({
    productId: String(p.productID?._id || p.productID || p.productId),
    filterPro: p.filterPro ? String(p.filterPro?._id || p.filterPro) : null,
    quantity: Math.max(1, Number(p.count ?? p.quantity) || 1),
    ...(p.shopId ? { shopId: String(p.shopId?._id || p.shopId) } : {}),
    selectedVariantId: p.selectedVariantId || null,
  }));

// Checkout address (CheckoutProd `Saddress` shape) -> estimator destination.
// No country yet -> null: the backend then uses the signed-in buyer's default
// address, or asks for one.
export const buildEstimateDestination = (address) => {
  if (!address || !address.country) return null;
  return {
    country: address.country,
    state: address.state || address.State || "",
    city: address.city || "",
    postal_code: address.postal_code || address.postalCode || "",
    line1: address.line1 || address.Address1 || "",
    firstName: address.firstName || "",
    lastName: address.lastName || "",
  };
};

// Which destination the checkout box ESTIMATES for (Location Context):
//   - the delivery address once the consumer picked / typed one, and always
//     on the payment step (the order ships there and is charged for it);
//   - before that, an explicit Target Location wins over the automatically
//     pre-filled default address (the consumer is shopping for that place);
//   - with no address at all, the Target Location or approximate IP location.
// Returns { address, provisional } -- `provisional` = show it as an estimate.
export const pickEstimateAddress = ({ address, provisionalDestination, paymentStep = false, addressTouched = false }) => {
  const usable = Boolean(provisionalDestination?.country) && !paymentStep
    && (!address?.country || (provisionalDestination.source === "target" && !addressTouched));
  return usable ? { address: provisionalDestination, provisional: true } : { address, provisional: false };
};

const useServerEstimate = ({ items, destination, token, enabled = true, fetcher, namespace }) => {
  const key = enabled && items?.length ? JSON.stringify({ namespace, items, destination, signedIn: Boolean(token) }) : "";
  const [state, setState] = useState({ key: "", status: "idle", data: null });
  const [nonce, setNonce] = useState(0);
  const latest = useRef({ items, destination, token, fetcher });
  latest.current = { items, destination, token, fetcher };
  const hadResult = useRef(false);

  useEffect(() => {
    if (!key) {
      setState({ key: "", status: "idle", data: null });
      return undefined;
    }
    const hit = cache.get(key);
    if (hit && hit.expiresAt > Date.now()) {
      hadResult.current = true;
      setState({ key, status: "ready", data: hit.data });
      return undefined;
    }
    setState({ key, status: "loading", data: null });
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const { items: currentItems, destination: currentDestination, token: currentToken, fetcher: currentFetcher } = latest.current;
      currentFetcher(currentToken, { items: currentItems, destination: currentDestination }, controller.signal)
        .then((data) => {
          if (controller.signal.aborted) return;
          if (!data || data.error) {
            setState({ key, status: "error", data: null, code: data?.code || null });
            return;
          }
          hadResult.current = true;
          cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
          setState({ key, status: "ready", data });
        })
        .catch(() => {
          if (!controller.signal.aborted) setState({ key, status: "error", data: null });
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [key, nonce]);

  // Drops the cached entry and fetches again (e.g. after SHIPPING_CHANGED).
  const refresh = useCallback(() => {
    if (key) cache.delete(key);
    setNonce((n) => n + 1);
  }, [key]);

  // A result computed for other inputs is never exposed as the current one.
  const current = state.key === key ? state : { status: key ? "loading" : "idle", data: null };
  return { status: current.status, data: current.data, code: current.code || null, updating: current.status === "loading" && hadResult.current, refresh };
};

const useShippingEstimate = (args) => useServerEstimate({ ...args, fetcher: getShippingEstimate, namespace: "estimate" });

// Internal Sale Simulation (management views only; the server returns 403 to
// anyone but the product's Seller / Provider / Family). Same inputs, same
// debounce/cache rules as the customer estimate it explains.
export const useSaleSimulation = (args) => useServerEstimate({ ...args, fetcher: getSaleSimulation, namespace: "simulation" });

export const __clearShippingEstimateCache = () => cache.clear();

export default useShippingEstimate;
